import { T, useGT } from 'gt-react';
import { ArrowDownRightIcon, ArrowUpRightIcon } from '@phosphor-icons/react';
import {
  formatReportNumber,
  KpiComponentSchema,
  ReportAggregateFunctionSchema,
  reportAggregate,
  type KpiComponent,
  type ReportAggregateFunction,
} from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { defineComponentType, type ComponentRegistry } from '../registry';
import {
  useComponentDataset,
  useReportDataset,
  type ReportDataset,
  type ReportDatasetRow,
} from '../ReportDataContext';
import { useComponentTextContext, useIsFramed } from '../ComponentFrame';
import {
  columnFormat,
  formatReportText,
  reportNumberFormatFor,
  templateValue,
} from '../format/reportText';
import { cn } from '@/lib/utils';
import { NumberFormatFields } from '../format/NumberFormatFields';
import {
  ChartEmptyState,
  columnLabelOf,
  ColumnSelect,
  ConfigRow,
  DatasetSelectRow,
  ToggleRow,
  ConfigSelectTrigger,
} from './shared';
import { NO_AXIS } from './chartConstants';
import { TONE_STYLES } from './tableFormatting';

/** What a KPI shows: its value and, with a comparison, the base and change. */
export type KpiView = {
  value: number | null;
  base: number | null;
  /** value − base. */
  delta: number | null;
  /** delta ÷ |base|; null when the base is 0 or missing. */
  ratio: number | null;
  /** Whether the change is good, given which way is better; null without one. */
  good: boolean | null;
};

function reduce(
  rows: readonly ReportDatasetRow[],
  column: string,
  fn: ReportAggregateFunction,
  aggregated: boolean,
): number | null {
  // Server aggregation already reduced the column to one row: read it.
  if (aggregated) {
    const value = rows[0]?.[column];
    return typeof value === 'number' ? value : null;
  }
  return reportAggregate(
    rows.map((row) => row[column] ?? null),
    fn,
  );
}

/** A KPI's numbers from its dataset's rows (or its Server aggregation). */
export function kpiView(
  component: Pick<
    KpiComponent,
    'valueKey' | 'fn' | 'comparison' | 'higherIsBetter'
  >,
  dataset: Pick<ReportDataset, 'rows' | 'aggregated'>,
): KpiView {
  const aggregated = Boolean(dataset.aggregated);
  const value = component.valueKey
    ? reduce(dataset.rows, component.valueKey, component.fn, aggregated)
    : null;
  const { comparison } = component;
  const base =
    comparison.kind === 'target'
      ? comparison.value
      : comparison.kind === 'column' && comparison.column
        ? reduce(dataset.rows, comparison.column, component.fn, aggregated)
        : null;
  if (value === null || base === null)
    return { value, base, delta: null, ratio: null, good: null };
  const delta = value - base;
  return {
    value,
    base,
    delta,
    ratio: base === 0 ? null : delta / Math.abs(base),
    good: delta === 0 ? null : delta > 0 === component.higherIsBetter,
  };
}

/** The status tone of a KPI's change: green when good, red when bad, gray when level. */
function kpiTone(good: boolean | null) {
  if (good === null) return TONE_STYLES.neutral;
  return good ? TONE_STYLES.positive : TONE_STYLES.negative;
}

function useFnLabels(): Record<ReportAggregateFunction, string> {
  const gt = useGT();
  const context = 'KPI aggregate function';
  return {
    sum: gt('Sum', { context }),
    avg: gt('Average', { context }),
    min: gt('Minimum', { context }),
    max: gt('Maximum', { context }),
    count: gt('Count', { context }),
  };
}

/** A KPI's change against its base: the tinted delta and what it's against. */
function KpiChange({
  view,
  baseLabel,
  format,
}: {
  view: KpiView & { delta: number };
  baseLabel: string;
  format: Parameters<typeof formatReportNumber>[1];
}) {
  const gt = useGT();
  const Arrow = view.delta >= 0 ? ArrowUpRightIcon : ArrowDownRightIcon;
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-xs">
      <span
        className="inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 font-medium tabular-nums"
        style={kpiTone(view.good)}
        data-testid="kpi-change"
      >
        <Arrow className="h-3 w-3" aria-hidden />
        {view.ratio === null
          ? formatReportNumber(view.delta, format)
          : formatReportNumber(view.ratio, { style: 'percent' })}
      </span>
      <span className="truncate text-muted-foreground">
        {gt('vs {base} {value}', {
          base: baseLabel,
          value: formatReportNumber(view.base, format),
          context: 'KPI comparison, e.g. "vs Budget $1,200"',
        })}
      </span>
    </p>
  );
}

/** A KPI whose headline is text over its dataset's totals, with a line under it. */
function KpiTextValue({ component }: { component: KpiComponent }) {
  const context = useComponentTextContext(component);
  const framed = useIsFramed();
  const value = component.value ?? '';
  const shown =
    templateValue(value, context) == null
      ? null
      : formatReportText(value, context);
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col justify-center gap-1',
        !framed && 'flex-1 px-3 pb-2',
      )}
    >
      <p
        className="text-2xl font-bold tabular-nums"
        title={shown ?? undefined}
        data-testid="kpi-value"
      >
        {shown ?? <span className="text-muted-foreground">—</span>}
      </p>
      {component.subtext && (
        <p className="text-xs text-muted-foreground">
          {formatReportText(component.subtext, context)}
        </p>
      )}
    </div>
  );
}

/** The KPI's value column's format, as its dataset states it, when the KPI sets none. */
function kpiFormat(
  component: KpiComponent,
  dataset: ReportDataset,
): Parameters<typeof formatReportNumber>[1] {
  if (component.fn === 'count') return { decimals: 0 };
  if (component.format) return component.format;
  const stated = component.valueKey
    ? columnFormat(dataset.columns, component.valueKey)
    : null;
  return stated ? reportNumberFormatFor(stated) : undefined;
}

function KpiRenderer({ component }: { component: KpiComponent }) {
  const gt = useGT();
  const dataset = useComponentDataset(component);
  if (component.value != null) return <KpiTextValue component={component} />;
  if (!dataset || !component.valueKey)
    return (
      <ChartEmptyState
        hasData={dataset !== null}
        icon={<ArrowUpRightIcon className="h-8 w-8 text-muted-foreground/40" />}
      />
    );
  const view = kpiView(component, dataset);
  const format = kpiFormat(component, dataset);
  const shown = formatReportNumber(view.value, format);
  const { comparison } = component;
  const baseLabel =
    comparison.kind === 'column'
      ? columnLabelOf(dataset.columns, comparison.column)
      : gt('target', { context: 'KPI compared with a fixed target' });
  const { delta } = view;
  return (
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 pb-2">
      <p
        className="truncate text-3xl font-semibold tabular-nums"
        title={shown}
        data-testid="kpi-value"
      >
        {view.value === null ? '—' : shown}
      </p>
      {delta !== null && (
        <KpiChange
          view={{ ...view, delta }}
          baseLabel={baseLabel}
          format={format}
        />
      )}
    </div>
  );
}

function KpiConfigForm({
  component,
  onUpdate,
}: {
  component: KpiComponent;
  onUpdate: (updated: KpiComponent) => void;
}) {
  const gt = useGT();
  const fnLabels = useFnLabels();
  const dataset = useReportDataset(component.datasetKey);
  const columns = dataset?.columns ?? [];
  // Count counts any column's values; the others need Numbers.
  const valueColumns =
    component.fn === 'count'
      ? columns
      : columns.filter((column) => column.dataType === 'number');
  const { comparison } = component;
  return (
    <div className="space-y-4">
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) =>
          onUpdate({
            ...component,
            datasetKey,
            valueKey: null,
            comparison: { kind: 'none' },
          })
        }
      />
      <div className="flex gap-2">
        <div className="w-28">
          <ConfigRow label={gt('Show the', { context: 'KPI aggregate' })}>
            <Select
              value={component.fn}
              onValueChange={(fn) =>
                onUpdate({
                  ...component,
                  fn: ReportAggregateFunctionSchema.parse(fn),
                })
              }
            >
              <ConfigSelectTrigger aria-label={gt('Aggregate')}>
                <SelectValue />
              </ConfigSelectTrigger>
              <SelectContent>
                {ReportAggregateFunctionSchema.options.map((fn) => (
                  <SelectItem key={fn} value={fn}>
                    {fnLabels[fn]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ConfigRow>
        </div>
        <div className="min-w-0 flex-1">
          <ConfigRow label={gt('Of', { context: 'KPI value column' })}>
            <ColumnSelect
              value={component.valueKey}
              columns={
                valueColumns.length
                  ? valueColumns
                  : [
                      {
                        key: NO_AXIS,
                        label: gt('No numeric columns in result'),
                      },
                    ]
              }
              placeholder={gt('Column…')}
              onChange={(valueKey) => onUpdate({ ...component, valueKey })}
            />
          </ConfigRow>
        </div>
      </div>
      <ConfigRow label={gt('Compare with', { context: 'KPI comparison' })}>
        <Select
          value={comparison.kind}
          onValueChange={(kind) =>
            onUpdate({
              ...component,
              comparison:
                kind === 'target'
                  ? { kind: 'target', value: 0 }
                  : kind === 'column'
                    ? { kind: 'column', column: null }
                    : { kind: 'none' },
            })
          }
        >
          <ConfigSelectTrigger>
            <SelectValue />
          </ConfigSelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <T context="KPI comparison">Nothing</T>
            </SelectItem>
            <SelectItem value="target">
              <T context="KPI comparison">A target</T>
            </SelectItem>
            <SelectItem value="column">
              <T context="KPI comparison">
                Another column (Budget, Prior year)
              </T>
            </SelectItem>
          </SelectContent>
        </Select>
        {comparison.kind === 'target' && (
          <Input
            type="number"
            aria-label={gt('Target', { context: 'KPI comparison' })}
            value={comparison.value}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (Number.isFinite(value))
                onUpdate({
                  ...component,
                  comparison: { kind: 'target', value },
                });
            }}
          />
        )}
        {comparison.kind === 'column' && (
          <ColumnSelect
            value={comparison.column}
            columns={valueColumns.filter((c) => c.key !== component.valueKey)}
            placeholder={gt('Column…')}
            onChange={(column) =>
              onUpdate({ ...component, comparison: { kind: 'column', column } })
            }
          />
        )}
      </ConfigRow>
      {comparison.kind !== 'none' && (
        <ToggleRow
          label={gt('Higher is better', { context: 'KPI comparison' })}
          id={`kpi-higher-${component.id}`}
          checked={component.higherIsBetter}
          onCheckedChange={(higherIsBetter) =>
            onUpdate({ ...component, higherIsBetter })
          }
        />
      )}
      {component.fn !== 'count' && (
        <ConfigRow label={gt('Number format')}>
          <NumberFormatFields
            idPrefix={`kpi-${component.id}`}
            value={component.format}
            onChange={(format) => onUpdate({ ...component, format })}
          />
        </ConfigRow>
      )}
    </div>
  );
}

function KpiPalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        {/* The number, then its change beside a small up arrow. */}
        <rect x="8" y="9" width="28" height="9" rx="1.5" fill="currentColor" />
        <rect
          x="8"
          y="23"
          width="12"
          height="5"
          rx="1"
          fill={TONE_STYLES.positive.backgroundColor}
        />
        <path
          d="M10.5 26.5 L13 24 L15.5 26.5"
          stroke={TONE_STYLES.positive.color}
          strokeWidth="1"
        />
        <rect
          x="23"
          y="24"
          width="13"
          height="3"
          rx="1"
          fill="currentColor"
          fillOpacity="0.3"
        />
      </svg>
    </div>
  );
}

export const kpiDefinition = defineComponentType<KpiComponent>({
  type: 'kpi',
  label: 'KPI',
  defaultTitle: (component, { dataset, gt }) => {
    const column = component.valueKey
      ? (dataset?.columns.find((c) => c.key === component.valueKey)?.label ??
        component.valueKey)
      : null;
    if (!column) return 'KPI';
    const context = 'KPI default title, e.g. "Sum of Net"';
    const titles: Record<ReportAggregateFunction, () => string> = {
      sum: () => gt('Sum of {column}', { column, context }),
      avg: () => gt('Average of {column}', { column, context }),
      min: () => gt('Minimum of {column}', { column, context }),
      max: () => gt('Maximum of {column}', { column, context }),
      count: () => gt('Count of {column}', { column, context }),
    };
    return titles[component.fn]();
  },
  description:
    'Dataset-bound scorecard: one number, the valueKey column reduced by fn (sum|avg|min|max|count), in format {style: number|currency|percent, decimals?, currency?, compact?} (absent = the column\'s own format); comparison {kind: "none"} | {kind: "target", value} | {kind: "column", column} shows the change, green when it goes the higherIsBetter way. Or `value`: the headline as text over the dataset\'s totals with {column} placeholders (e.g. "{share}"), with `subtext` under it.',
  columnRefFields: ['valueKey'],
  version: 1,
  size: { w: 3, h: 3, minW: 2, minH: 2 },
  make: (id) => KpiComponentSchema.parse({ type: 'kpi', id }),
  Renderer: KpiRenderer,
  ConfigForm: KpiConfigForm,
  Preview: KpiPalettePreview,
});

export const kpiWidgetRegistry: ComponentRegistry = {
  [kpiDefinition.type]: kpiDefinition,
};
